import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';
import { saveEngagementDraft } from './documentEngagement.slice';
import { copyObject } from '../utils/common';
import { gotoNextMenu, setAssessmentError } from './assessment.slice';
import { CulturalConsiderationsModel } from '../Models/CulturalConsiderations/CulturalConsiderationsModel.model';
import { SummaryNoteTypeEnum } from '../Enum/SummaryNoteTypeEnum';
import { preparePriorAssessmentForNewDraft, sanitizeAiFlagsForEmptyAnswers } from '../utils/aiFlagHelpers';

const culturalConsiderationsSlice = createSlice({
    name: 'culturalConsiderationsSlice',
    initialState: { culturalConsiderationsInfo: new CulturalConsiderationsModel(), isFetchingCulturalConsiderationsInfo: false },
    reducers: {
        _setIsFetchingCulturalConsiderationsInfo(state, action) {
            state.isFetchingCulturalConsiderationsInfo = action.payload;
        },
        _setCulturalConsiderationsInfo(state, action) {
            state.culturalConsiderationsInfo = action.payload;
        },
    },
});

const { _setIsFetchingCulturalConsiderationsInfo, _setCulturalConsiderationsInfo } = culturalConsiderationsSlice.actions;

export const setCulturalConsiderationsInfo =
    (info: CulturalConsiderationsModel): AppThunk =>
    async (dispatch) => {
        dispatch(_setCulturalConsiderationsInfo(info));
    };

export const setIsFetchingCulturalConsiderationsInfo =
    (isFetching: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingCulturalConsiderationsInfo(isFetching));
    };

export const saveCulturalConsiderationsInfo =
    (culturalConsiderationsModel: CulturalConsiderationsModel, _saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch) => {
        const isUpdate = Boolean(culturalConsiderationsModel.Id);
        const url = isUpdate
            ? `${API_URL}/culturalconsiderations/updateculturalconsideration/`
            : `${API_URL}/culturalconsiderations/insertculturalconsideration/`;

        try {
            const response = await axios.put(url, culturalConsiderationsModel);
            const savedCulturalConsiderations = isUpdate ? culturalConsiderationsModel : response.data;
            if (Boolean(response.data)) {
                dispatch(
                    setCulturalConsiderationsInfo(
                        isUpdate ? culturalConsiderationsModel : { ...culturalConsiderationsModel, Id: response.data.Id }
                    )
                );
            }
            const isDraftSaved = await dispatch(prepareCulturalConsiderationsNotes(savedCulturalConsiderations));
            if (gotoNext && isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        }
    };

const prepareCulturalConsiderationsNotes =
    (culturalConsiderations: CulturalConsiderationsModel): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(culturalConsiderations.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.CulturalConsiderations);
            summaryNotes.push({
                Id: culturalConsiderations.MemberId,
                CptCode: '',
                Zcode: '',
                Note: 'Cultural Considerations Completed',
                Type: SummaryNoteTypeEnum.CulturalConsiderations,
                IsConfirm: true,
                DisplayName: 'Cultural Considerations',
            });

            engagementDraft.SummaryNotes = summaryNotes;

            const updatedMemberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                CulturalConsiderationsStatus: culturalConsiderations.AssessmentStatus,
            };
            const updatedEngagementDraft = {
                ...engagementDraft,
                CulturalConsiderationsId: culturalConsiderations.Id,
            };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
                EngagementDraft: updatedEngagementDraft,
            };
            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const fetchCulturalConsiderationsInfo =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingCulturalConsiderationsInfo(true));
        const response = await axios.get(`${API_URL}/culturalconsiderations/getculturalconsiderationsinfobyid?id=${id}`);
        if (response.data) {
            sanitizeAiFlagsForEmptyAnswers(response.data);
            dispatch(setCulturalConsiderationsInfo(response.data));
        } else {
            dispatch(setCulturalConsiderationsInfo(new CulturalConsiderationsModel()));
        }

        dispatch(setIsFetchingCulturalConsiderationsInfo(false));
    };

export const fetchCulturalConsiderationsInfoByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingCulturalConsiderationsInfo(true));
        const response = await axios.get(`${API_URL}/culturalconsiderations/getculturalconsiderationsinfobymemberid?memberId=${memberId}`);
        if (response.data) {
            preparePriorAssessmentForNewDraft(response.data);
            if (response.data.CulturalBarriers) {
                response.data.CulturalBarriers.Id = null;
            }
            dispatch(setCulturalConsiderationsInfo(response.data));
        } else {
            dispatch(setCulturalConsiderationsInfo(new CulturalConsiderationsModel()));
        }
        dispatch(setIsFetchingCulturalConsiderationsInfo(false));
    };

export default culturalConsiderationsSlice.reducer;
